import { create } from 'zustand'
import { shared } from '@/lib/inflight'
import { flushOutbox, onOutboxSent } from '@/lib/outbox'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { nativeAppleAvailable, nativeAppleSignIn } from '@/lib/appleSignIn'
import { supabase } from '@/lib/supabase'
import { isSupabaseConfigured } from '@/lib/config'
import { localdb } from '@/lib/localdb'
import { newLocalProfile } from '@/lib/progress'
import { allSkins, equippedSkinId, isDefaultAvatar } from '@/data/avatar'
import { applyPendingSource } from '@/lib/attribution'
import { getVerseForDate } from '@/data/bible/questions'
import { petUnlocked, type PetProgress } from '@/data/pets'
import { normalizeBook, normalizeTranslation, normalizeVerseReference, type CardAbout } from '@/data/cardAbout'
import { useSettings } from './settings'
import type { Profile, AvatarSpec } from '@/types'

type Mode = 'online' | 'local'

/** Character chosen during account creation, before the account exists. */
const PENDING_CHARACTER_KEY = 'va.pendingCharacter'
// Per-account list of exclusive skins this DEVICE has already seen on the
// account. Device-local on purpose: it records what has been shown, not what
// is owned — the entitlement itself lives in owned_skins, server-side.
const SEEN_SKINS_KEY = 'va.skinsSeen'
// Where Supabase sends a NATIVE sign-in when the provider is done: an https
// page on the site that hands the session on to the app's URL scheme (see
// signInOAuth). It must be on Supabase's redirect allow-list, exactly as
// written, trailing slash included. Overridable so a preview site can test it.
const NATIVE_AUTH_BRIDGE_URL =
  import.meta.env.VITE_NATIVE_AUTH_BRIDGE_URL || 'https://versearcade.org/auth/native/'

interface DbProfileRow {
  id: string
  username: string
  display_name: string | null
  avatar_emoji: string
  xp: number
  level: number
  current_streak: number
  longest_streak: number
  streak_freezes: number
  last_played_on: string | null
  total_plays: number
  sound_enabled: boolean
  haptics_enabled: boolean
  reduce_motion: boolean
  onboarded: boolean
  avatar_border: string | null
  avatar_badge: string | null
  card_background: string | null
  pet?: string | null
  favorite_verse?: string | null
  favorite_book?: string | null
  favorite_translation?: string | null
  reading_translation?: string | null
  // Not yet a real column — an online account reads back null and falls back to
  // the emoji until a profiles.avatar_character migration lands.
  avatar_character?: AvatarSpec | null
  shared_days?: string[] | null
  live_battles?: number | null
  battle_wins?: number | null
  owned_items?: string[] | null
  owned_skins?: string[] | null
  xp_boosts: number | null
  founder?: boolean | null
  is_admin?: boolean | null
  denomination?: string | null
  referral_code?: string | null
}

function mapRow(r: DbProfileRow): Profile {
  return {
    id: r.id,
    username: r.username,
    displayName: r.display_name ?? undefined,
    avatarEmoji: r.avatar_emoji,
    xp: r.xp,
    level: r.level,
    currentStreak: r.current_streak,
    longestStreak: r.longest_streak,
    streakFreezes: r.streak_freezes,
    lastPlayedOn: r.last_played_on,
    totalPlays: r.total_plays,
    soundEnabled: r.sound_enabled,
    hapticsEnabled: r.haptics_enabled,
    reduceMotion: r.reduce_motion,
    onboarded: r.onboarded,
    avatarBorder: r.avatar_border ?? 'default',
    avatarBadge: r.avatar_badge ?? null,
    cardBackground: r.card_background ?? null,
    pet: r.pet ?? null,
    favoriteVerse: r.favorite_verse ?? null,
    favoriteBook: r.favorite_book ?? null,
    favoriteTranslation: r.favorite_translation ?? null,
    readingTranslation: r.reading_translation ?? null,
    avatarCharacter: r.avatar_character ?? null,
    sharedDays: r.shared_days ?? [],
    liveBattles: r.live_battles ?? 0,
    battleWins: r.battle_wins ?? 0,
    ownedItems: r.owned_items ?? [],
    ownedSkins: r.owned_skins ?? [],
    xpBoosts: r.xp_boosts ?? 0,
    founder: r.founder ?? false,
    isAdmin: r.is_admin ?? false,
    denomination: r.denomination ?? null,
    referralCode: r.referral_code ?? null,
  }
}

// Push profile prefs into the local settings store so juice reflects them.
function syncSettingsFromProfile(p: Profile) {
  useSettings.getState().set({
    soundEnabled: p.soundEnabled,
    hapticsEnabled: p.hapticsEnabled,
    reduceMotion: p.reduceMotion,
    // Only an explicit choice travels: a null leaves this device's own pick.
    ...(p.readingTranslation ? { readingTranslation: p.readingTranslation } : {}),
  })
}

interface AuthState {
  ready: boolean
  mode: Mode
  profile: Profile | null
  isAuthed: boolean
  error: string | null

  init: () => Promise<void>
  refreshProfile: () => Promise<void>
  loadReferral: () => Promise<void>
  setProfileLocal: (p: Profile) => void

  beginGuestClaim: () => void
  claimGuestProgress: () => Promise<void>

  signUpEmail: (email: string, password: string, username: string) => Promise<{ needsConfirmation: boolean }>
  signIn: (identifier: string, password: string) => Promise<void>
  signInOAuth: (provider: 'google' | 'apple') => Promise<void>
  completeNativeOAuth: (url: string) => Promise<void>
  startAsGuest: (username: string, emoji: string, character?: AvatarSpec | null) => void
  /** Park the character picked during account creation until the account
   *  actually exists (see applyPendingCharacter). */
  setPendingCharacter: (spec: AvatarSpec | null) => void
  applyPendingCharacter: () => void
  /** Put on a skin that was GRANTED since this device last looked — see the
   *  implementation for the three rules that stop it repainting anybody. */
  autoEquipGrantedSkin: () => void

  updateProfile: (patch: Partial<Profile>) => Promise<void>
  changeUsername: (next: string) => Promise<{ ok: boolean; error?: string }>
  setCosmetics: (patch: { border?: string; badge?: string | null }) => Promise<{ ok: boolean; error?: string }>
  setAvatarCharacter: (spec: AvatarSpec) => void
  setCardBackground: (key: string) => Promise<{ ok: boolean; error?: string }>
  /**
   * Equip a pet, or null for none. The server is the gate (0064); `progress`
   * is the guest-mode gate and comes from the caller — see lib/petProgress for
   * why this store doesn't gather it itself.
   */
  setPet: (id: string | null, progress?: PetProgress) => Promise<{ ok: boolean; error?: string }>
  /**
   * Set what the card says about you — a favorite verse, book and translation
   * (data/cardAbout). All three at once; null clears one. Both sides validate
   * against the same fixed catalogs, so nothing typed is ever stored.
   */
  setCardAbout: (about: CardAbout) => Promise<{ ok: boolean; error?: string }>
  recordShare: (dropDate: string) => void
  grantItem: (itemId: string) => void
  grantSkin: (skinId: string) => void
  /** False when the server refused or never answered — the account still exists, so nobody is signed out. */
  deleteAccount: () => Promise<boolean>
  signOut: () => Promise<void>
}

// The signed-in player's last profile, kept on the device. Offline, the
// profile read fails after its retries and an account with no profile is
// routed as signed out — every tab sat on the splash with no network. So
// startup shows THIS straight away and refreshes it behind (which also takes
// a round trip off every online launch), and a refresh that fails keeps it.
// Keyed by user id, so a different account on the same phone never sees it;
// cleared on sign-out and on erasure. It grants nothing — every write still
// goes through the server, which decides.
const PROFILE_CACHE = 'va.profileCache'
function readCachedProfile(uid: string): Profile | null {
  try {
    const c = JSON.parse(localStorage.getItem(PROFILE_CACHE) ?? 'null')
    return c && c.uid === uid && c.profile ? (c.profile as Profile) : null
  } catch { return null }
}
function writeCachedProfile(p: Profile) {
  try { localStorage.setItem(PROFILE_CACHE, JSON.stringify({ uid: p.id, profile: p })) } catch { /* storage full or blocked */ }
}
function clearCachedProfile() {
  try { localStorage.removeItem(PROFILE_CACHE) } catch { /* nothing to clear */ }
}

export const useAuth = create<AuthState>((set, get) => ({
  ready: false,
  mode: isSupabaseConfigured ? 'online' : 'local',
  profile: null,
  isAuthed: false,
  error: null,

  async init() {
    if (!supabase) {
      // LOCAL mode: restore any guest profile.
      const p = localdb.getProfile()
      if (p) syncSettingsFromProfile(p)
      set({ ready: true, mode: 'local', profile: p, isAuthed: !!p })
      return
    }
    supabase.auth.onAuthStateChange(async (_evt, session) => {
      if (session) {
        set({ mode: 'online' })
        // If this session is a guest upgrading, fold their device progress into
        // the freshly-created account before we read it back. No-op otherwise.
        await get().claimGuestProgress()
        await get().refreshProfile()
      } else {
        // Cloud session ended — fall back to any local guest profile so the
        // user isn't kicked back to onboarding.
        const p = localdb.getProfile()
        if (p) syncSettingsFromProfile(p)
        set({ mode: p ? 'local' : 'online', profile: p, isAuthed: !!p })
      }
    })

    const { data } = await supabase.auth.getSession()
    if (data.session) {
      // The device's copy first, so the app opens with no network and without
      // waiting on one; the server's copy replaces it a round trip later.
      const cached = get().profile ? null : readCachedProfile(data.session.user.id)
      if (cached) {
        syncSettingsFromProfile(cached)
        set({ ready: true, mode: 'online', isAuthed: true, profile: cached })
        void get().refreshProfile()
        return
      }
      await get().refreshProfile()
      set({ ready: true, mode: 'online', isAuthed: true })
      return
    }

    // No cloud session: restore a guest profile from this device if one exists,
    // so guests (the default onboarding path) survive a refresh.
    const p = localdb.getProfile()
    if (p) syncSettingsFromProfile(p)
    set({ ready: true, mode: p ? 'local' : 'online', profile: p, isAuthed: !!p })
  },

  refreshProfile: shared(async () => {
    if (!supabase) return
    // Use the LOCAL session (getSession) rather than getUser(): getUser() makes a
    // network round-trip and, called from inside onAuthStateChange, triggers a
    // request storm / loop. getSession reads the already-set token synchronously.
    const { data: s } = await supabase.auth.getSession()
    const user = s.session?.user
    if (!user) {
      set({ profile: null, isAuthed: false })
      return
    }
    // maybeSingle (not single) so a transient 0-row read doesn't throw
    // "Cannot coerce the result to a single JSON object" — which happened when a
    // refresh fired a hair before the auth token was attached to the request.
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle()
    if (error) {
      // Offline or the server didn't answer: keep what the device last saw
      // rather than leaving a signed-in player with no profile at all.
      const cached = get().profile ? null : readCachedProfile(user.id)
      if (cached) {
        syncSettingsFromProfile(cached)
        set({ profile: cached, isAuthed: true })
      }
      set({ error: error.message })
      return
    }
    if (!data) {
      // Row not visible yet (RLS/timing on a brand-new OAuth user). Don't clear an
      // existing profile or surface an error — a later auth event resolves it.
      return
    }
    const p = mapRow(data as DbProfileRow)
    syncSettingsFromProfile(p)
    set({ profile: p, isAuthed: true, error: null })
    get().applyPendingCharacter()
    get().autoEquipGrantedSkin()
    void get().loadReferral()
    // The profile just came back from the server, so a network is here: send
    // whatever this account parked while it wasn't (lib/outbox.ts).
    void flushOutbox()
    // Which network sent this person, if a tracked link said so (0106).
    void applyPendingSource()
  }),

  // Apply any pending referral code (captured from a ?ref=… link before signup),
  // then load my code + referral count into the profile.
  loadReferral: shared(async () => {
    if (!supabase) return
    try {
      const pending = localStorage.getItem('va.ref')
      if (pending) {
        const { data } = await supabase.rpc('apply_referral', { p_code: pending })
        if ((data as { ok?: boolean })?.ok) localStorage.removeItem('va.ref')
      }
      const { data } = await supabase.rpc('my_referral_stats')
      const stats = data as { code?: string; count?: number } | null
      const cur = get().profile
      if (cur && stats) {
        set({ profile: { ...cur, referralCode: stats.code ?? cur.referralCode, referralCount: stats.count ?? 0 } })
      }
    } catch {
      /* referral is non-critical — never block the app on it */
    }
  }),

  setProfileLocal(p) {
    if (get().mode === 'local') localdb.saveProfile(p)
    set({ profile: p })
  },

  // Snapshot the current guest's progress just before an account-creation flow.
  // Stored in localStorage so it survives the OAuth redirect (web reloads the
  // page). Consumed once by claimGuestProgress after the session lands.
  beginGuestClaim() {
    if (get().mode !== 'local') return
    const p = get().profile
    if (!p) return
    const plays = Object.entries(localdb.getPlays()).map(([dropDate, v]) => ({
      drop_date: dropDate,
      score: v.result.score,
      time_ms: v.result.timeMs,
      correct_count: v.result.correctCount,
      total_questions: v.result.totalQuestions,
      combo_max: v.result.comboMax,
      xp_earned: v.outcome.xpEarned ?? 0,
    }))
    localdb.setPendingClaim({ profile: p, cards: localdb.getCards(), plays })
    // claim_guest_progress doesn't carry avatar_character, so a guest who built
    // a character and then signed up used to arrive at their new account with
    // the emoji fallback. Park it like a sign-up pick — but never over one: the
    // picker on the sign-up screen ran later and is the more recent choice.
    try {
      if (p.avatarCharacter && !localStorage.getItem(PENDING_CHARACTER_KEY)) {
        localStorage.setItem(PENDING_CHARACTER_KEY, JSON.stringify(p.avatarCharacter))
      }
    } catch {
      /* ignore */
    }
  },

  // Fold a pending guest snapshot into the just-created account (username, emoji,
  // XP, streak, cards and completed plays), then clear the local guest mirror so
  // we never re-claim or show stale guest state. Safe to call on every sign-in:
  // the server RPC only writes into a pristine, never-played profile.
  async claimGuestProgress() {
    if (!supabase) return
    const snap = localdb.getPendingClaim()
    if (!snap) return
    const { data: s } = await supabase.auth.getSession()
    if (!s.session) return
    try {
      // Seed the shared verse rows for every completed day first, so the plays
      // FK (plays.drop_date -> daily_verses) resolves. Verse content is derived
      // deterministically from the date, so any client seeds identical rows.
      for (const pl of snap.plays) {
        const v = getVerseForDate(pl.drop_date)
        await supabase.rpc('ensure_daily_verse', {
          p_drop_date: pl.drop_date,
          p_translation: v.translation,
          p_reference: v.reference,
          p_book: v.book,
          p_chapter: v.chapter,
          p_verse_start: v.verseStart,
          p_verse_end: v.verseEnd ?? null,
          p_text: v.text,
          p_theme: v.theme ?? null,
          p_questions: v.questions,
          p_facts: v.facts,
        })
      }
      const p = snap.profile
      const { error } = await supabase.rpc('claim_guest_progress', {
        p_username: p.username,
        p_emoji: p.avatarEmoji,
        p_display_name: p.displayName ?? p.username,
        p_xp: p.xp,
        p_level: p.level,
        p_current_streak: p.currentStreak,
        p_longest_streak: p.longestStreak,
        p_streak_freezes: p.streakFreezes,
        p_last_played_on: p.lastPlayedOn ?? null,
        p_total_plays: p.totalPlays,
        p_cards: snap.cards,
        p_plays: snap.plays,
        // Clear this device's guest_opens rows so the just-migrated progress
        // isn't also counted as a separate guest on the leaderboard.
        p_guest_id: localdb.getGuestId(),
      })
      if (error) {
        // Keep the snapshot for a later retry rather than losing the guest's data.
        set({ error: error.message })
        return
      }
      // Migrated — the account is now the source of truth. Drop the local mirror.
      localdb.clear()
      localdb.clearPendingClaim()
    } catch (e) {
      set({ error: (e as Error).message })
    }
  },

  async signUpEmail(email, password, username) {
    if (!supabase) throw new Error('Backend not configured')
    set({ error: null })
    // If a guest is upgrading, snapshot their progress before the account lands.
    get().beginGuestClaim()
    const emailRedirectTo =
      import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin + '/auth/callback'
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username, display_name: username }, emailRedirectTo },
    })
    if (error) {
      set({ error: error.message })
      throw error
    }
    // Signing up with an address that already has an account comes back as a
    // user with no identities and no session — Supabase's deliberate
    // anti-enumeration shape. Say so, rather than leaving them waiting on a
    // confirmation email that will never arrive (which is what the branch
    // below would tell them).
    if (data.user && (data.user.identities?.length ?? 0) === 0) {
      const msg = 'That email already has an account — try signing in instead.'
      set({ error: msg })
      throw new Error(msg)
    }
    // With email confirmation ON, signUp returns no session — the user must
    // confirm before they're authed. Report that so the UI doesn't bounce them
    // straight into a protected route (which would kick back to onboarding).
    // With it OFF (the intended setup — see docs/SETUP-SUPABASE.md) a session
    // comes back immediately and they go straight into the game.
    if (!data.session) return { needsConfirmation: true }
    await get().refreshProfile()
    return { needsConfirmation: false }
  },

  async signIn(identifier, password) {
    if (!supabase) throw new Error('Backend not configured')
    set({ error: null })
    // A guest may be signing into an account they just made; snapshot in case
    // it's fresh. (The server ignores the claim for established accounts.)
    get().beginGuestClaim()
    const id = identifier.trim()

    // Email path: sign in directly.
    if (id.includes('@')) {
      const { error } = await supabase.auth.signInWithPassword({ email: id, password })
      if (error) {
        set({ error: error.message })
        throw error
      }
      await get().refreshProfile()
      return
    }

    // Username path: an edge function resolves the username to its account and
    // validates the password server-side (email never touches the browser),
    // then we adopt the returned session.
    const { data, error } = await supabase.functions.invoke('username-login', {
      body: { identifier: id, password },
    })
    if (error || !data?.session) {
      const msg = 'Invalid username or password'
      set({ error: msg })
      throw new Error(msg)
    }
    const { error: sessErr } = await supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    })
    if (sessErr) {
      set({ error: sessErr.message })
      throw sessErr
    }
    await get().refreshProfile()
  },

  async signInOAuth(provider) {
    if (!supabase) throw new Error('Backend not configured')
    // Snapshot guest progress now — the web flow reloads the page on redirect,
    // so this must be persisted before we hand off to the provider.
    get().beginGuestClaim()
    const native = Capacitor.isNativePlatform()
    // iOS + Apple: the system sheet first (lib/appleSignIn.ts) — no web view at
    // all. The token goes to Supabase directly and onAuthStateChange finishes
    // the sign-in exactly as a password sign-in does. A cancelled sheet stops
    // here; anything else that keeps the sheet from working (no entitlement in
    // this build, Supabase not yet told the bundle id) falls through to the
    // in-app browser below, so the button always signs somebody in.
    if (provider === 'apple' && nativeAppleAvailable()) {
      const r = await nativeAppleSignIn(NATIVE_AUTH_BRIDGE_URL)
      if (r.kind === 'cancelled') return
      if (r.kind === 'token') {
        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'apple',
          token: r.token,
          nonce: r.nonce,
        })
        if (!error) return
        // A rejected token is almost always the audience (bundle id) missing
        // from the Supabase Apple provider. Say so where a developer looks,
        // then take the door that is known to work.
        console.warn('[auth] native Apple token refused, using the browser:', error.message)
      } else {
        console.warn('[auth] native Apple sheet unavailable, using the browser:', r.reason)
      }
    }
    // Native: Supabase must redirect to an https page (the bridge, below), which
    // hands the session on to com.versearcade.app://auth/callback. Web: the
    // site's own callback route, where detectSessionInUrl finishes it.
    const redirectTo = native
      ? NATIVE_AUTH_BRIDGE_URL
      : import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin + '/auth/callback'
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      // On native we open the provider URL ourselves (in an in-app browser) so we
      // can catch the deep-link redirect back into the app.
      options: { redirectTo, skipBrowserRedirect: native },
    })
    if (error) {
      set({ error: error.message })
      throw error
    }
    if (native && data?.url) {
      // Open the provider IN THE APP — SFSafariViewController on iOS, a Custom
      // Tab on Android — never the system browser. App Review rejected a build
      // that bounced out to Safari to sign in ("taken to the default web
      // browser… a poor user experience", 2026-09-10) and named the Safari View
      // Controller as what they accept.
      //
      // The reason this used to bounce out: the in-app view refuses an
      // AUTOMATIC redirect to a custom URL scheme, so Supabase's 302 straight
      // to com.versearcade.app://auth/callback landed on a blank page. Sending
      // it to the https bridge instead (public/auth/native/index.html), which
      // offers the hop as a button, is what makes the in-app view usable — a
      // redirect the user TAPS is honoured every time. The deep link then
      // fires appUrlOpen -> completeNativeOAuth, which also closes this view.
      await Browser.open({ url: data.url })
    }
    // Web: the browser navigates automatically and detectSessionInUrl finishes it.
  },

  // Called from the native deep-link handler when the provider redirects back to
  // com.versearcade.app://auth/callback?code=... — exchanges the PKCE code for a
  // session, closes the in-app browser, and loads the profile.
  async completeNativeOAuth(url) {
    if (!supabase) return
    // The in-app browser closes on EVERY outcome, not only a good one. It used
    // to close only after a session landed, so a provider error (the player
    // backed out on Google's page) or a token Supabase refused left the bridge
    // page standing over the app with the reason hidden behind it.
    const closeBrowser = async () => {
      try { await Browser.close() } catch { /* already closed */ }
    }
    try {
      // The redirect can carry the session in one of two shapes:
      //  - PKCE flow:     com.versearcade.app://auth/callback?code=...
      //  - Implicit flow: com.versearcade.app://auth/callback#access_token=...&refresh_token=...
      // Supabase defaults to implicit, so the tokens arrive in the hash — the old
      // code only read ?code= and silently bailed, which is why sign-in returned
      // to the app but never authed. Handle BOTH so either flow works.
      const query = new URLSearchParams(url.includes('?') ? url.slice(url.indexOf('?') + 1).split('#')[0] : '')
      const hash = new URLSearchParams(url.includes('#') ? url.slice(url.indexOf('#') + 1) : '')

      const code = query.get('code')
      const accessToken = hash.get('access_token')
      const refreshToken = hash.get('refresh_token')

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code)
        if (error) {
          await closeBrowser()
          set({ error: error.message })
          return
        }
      } else if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
        if (error) {
          await closeBrowser()
          set({ error: error.message })
          return
        }
      } else {
        // No usable credentials — surface any provider error, then stop.
        await closeBrowser()
        const errDesc = query.get('error_description') || hash.get('error_description')
        if (errDesc) set({ error: errDesc })
        return
      }

      await closeBrowser()
      // Migrate any pending guest snapshot before we read the profile back, so
      // the account shows the user's chosen handle + progress from the first
      // render (idempotent — onAuthStateChange may have already run it).
      await get().claimGuestProgress()
      await get().refreshProfile()
    } catch (e) {
      await closeBrowser()
      set({ error: (e as Error).message })
    }
  },

  startAsGuest(username, emoji, character) {
    const p = { ...newLocalProfile(username, emoji), onboarded: true }
    if (character) p.avatarCharacter = character
    localdb.saveProfile(p)
    syncSettingsFromProfile(p)
    set({ profile: p, isAuthed: true, mode: 'local' })
  },

  // The character somebody builds while signing up exists before the account
  // does, and on the web an OAuth sign-up reloads the whole page mid-flow — so
  // it parks in localStorage rather than in memory, and lands on the profile
  // once there is a profile to land on.
  setPendingCharacter(spec) {
    try {
      if (spec) localStorage.setItem(PENDING_CHARACTER_KEY, JSON.stringify(spec))
      else localStorage.removeItem(PENDING_CHARACTER_KEY)
    } catch {
      /* private mode / storage disabled — the picker still works, it just
         doesn't survive the redirect. Never worth throwing over. */
    }
  },

  // Consume the parked character. Applied ONCE, and only onto an account that
  // hasn't got a look of its own yet: signing in to an established account on a
  // device where a half-finished sign-up left a pick behind must not repaint
  // that account's character.
  applyPendingCharacter() {
    let raw: string | null = null
    try {
      raw = localStorage.getItem(PENDING_CHARACTER_KEY)
    } catch {
      return
    }
    if (!raw) return
    const cur = get().profile
    if (!cur) return
    try {
      localStorage.removeItem(PENDING_CHARACTER_KEY)
    } catch {
      /* ignore */
    }
    if (cur.avatarCharacter && !isDefaultAvatar(cur.avatarCharacter)) return
    try {
      const spec = JSON.parse(raw) as AvatarSpec
      if (spec && typeof spec.skin === 'string' && typeof spec.robe === 'string') {
        get().setAvatarCharacter(spec)
      }
    } catch {
      /* corrupt blob — the account simply keeps the default look */
    }
  },

  // A skin GRANTED server-side — a creator collab, an operator grant — used to
  // arrive in complete silence: grant_skins() writes owned_skins and stops,
  // nothing in the app announces it (the mailbox carries gifts, buddy requests,
  // washings and news, never entitlements) and nothing puts it on. The REDEEM
  // path has always celebrated and equipped on the spot (CustomizeSection's
  // doRedeem), so the person handed a skin directly was the only one who never
  // saw it — which is exactly how the Porchlight collab sat unworn on the
  // creator's own account while his audience's copies equipped themselves.
  //
  // Three rules keep this from repainting anybody:
  //   1. Only when NOTHING is equipped. A look somebody chose is never
  //      overwritten, the same way applyPendingCharacter refuses an account
  //      that already has a character of its own.
  //   2. Only a skin that arrived AFTER this device first saw the account. The
  //      first run banks a baseline and equips nothing, so a skin somebody
  //      deliberately took off can't come back on the next reload — putting one
  //      down stays allowed, the rule pets already follow.
  //   3. Once, and the id is banked whether or not it went on, so taking it off
  //      sticks forever after.
  // Only `exclusive` skins qualify: earned and pass skins are announced by the
  // flows that grant them, and a priced one is never granted silently.
  autoEquipGrantedSkin() {
    const cur = get().profile
    if (!cur) return
    const owned = cur.ownedSkins ?? []
    if (owned.length === 0) return
    const exclusives = allSkins()
      .filter((sk) => sk.exclusive && owned.includes(sk.id))
      .map((sk) => sk.id)
    if (exclusives.length === 0) return

    const key = `${SEEN_SKINS_KEY}.${cur.id}`
    let seen: string[] | null = null
    try {
      const raw = localStorage.getItem(key)
      const parsed: unknown = raw ? JSON.parse(raw) : null
      seen = Array.isArray(parsed) ? (parsed as string[]) : null
    } catch {
      // Private mode, or a corrupt blob. Never repaint a character we have no
      // way to remember having repainted.
      return
    }
    const banked = Array.from(new Set([...(seen ?? []), ...exclusives]))
    try {
      localStorage.setItem(key, JSON.stringify(banked))
    } catch {
      // Can't bank it ⇒ don't wear it, or it comes back at every reload.
      return
    }
    // First run on this device: baseline only.
    if (seen === null) return

    const fresh = exclusives.find((id) => !seen!.includes(id))
    if (!fresh) return
    const spec = cur.avatarCharacter
    if (!spec || equippedSkinId(spec)) return
    get().setAvatarCharacter({ ...spec, skinId: fresh, regalia: null })
  },

  async updateProfile(patch) {
    const cur = get().profile
    if (!cur) return
    const next = { ...cur, ...patch }
    set({ profile: next })
    // Keep settings store aligned when prefs change.
    if (patch.soundEnabled !== undefined || patch.hapticsEnabled !== undefined || patch.reduceMotion !== undefined) {
      useSettings.getState().set({
        soundEnabled: next.soundEnabled,
        hapticsEnabled: next.hapticsEnabled,
        reduceMotion: next.reduceMotion,
      })
    }
    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return
    }
    const dbPatch: Record<string, unknown> = {}
    if (patch.username !== undefined) dbPatch.username = patch.username
    if (patch.displayName !== undefined) dbPatch.display_name = patch.displayName
    if (patch.avatarEmoji !== undefined) dbPatch.avatar_emoji = patch.avatarEmoji
    if (patch.avatarCharacter !== undefined) dbPatch.avatar_character = patch.avatarCharacter
    if (patch.denomination !== undefined) dbPatch.denomination = patch.denomination
    if (patch.pet !== undefined) dbPatch.pet = patch.pet
    if (patch.soundEnabled !== undefined) dbPatch.sound_enabled = patch.soundEnabled
    if (patch.hapticsEnabled !== undefined) dbPatch.haptics_enabled = patch.hapticsEnabled
    if (patch.reduceMotion !== undefined) dbPatch.reduce_motion = patch.reduceMotion
    if (patch.onboarded !== undefined) dbPatch.onboarded = patch.onboarded
    if (patch.readingTranslation !== undefined) dbPatch.reading_translation = patch.readingTranslation
    if (Object.keys(dbPatch).length) {
      await supabase.from('profiles').update(dbPatch).eq('id', cur.id)
    }
  },

  // Rename. Online, a SECURITY DEFINER RPC validates + enforces uniqueness so we
  // get a clean "taken"/"invalid" reason instead of a raw constraint error.
  // Guests rename freely on-device (no shared namespace to collide with).
  async changeUsername(next) {
    const cur = get().profile
    if (!cur) return { ok: false, error: 'No profile' }
    const clean = next.trim().toLowerCase().replace(/[^a-z0-9_]/g, '')
    if (clean.length < 2 || clean.length > 16) {
      return { ok: false, error: '2–16 letters, numbers, or underscores' }
    }
    if (clean === cur.username) return { ok: true }

    if (get().mode === 'local' || !supabase) {
      const p = { ...cur, username: clean, displayName: clean }
      localdb.saveProfile(p)
      set({ profile: p })
      return { ok: true }
    }

    const { data, error } = await supabase.rpc('set_username', { p_username: clean })
    if (error) return { ok: false, error: error.message }
    if (!data?.ok) {
      return { ok: false, error: data?.reason === 'taken' ? 'That username is taken' : 'Invalid username' }
    }
    set({ profile: { ...cur, username: data.username as string, displayName: data.username as string } })
    return { ok: true }
  },

  // Equip an avatar border / badge. The server refuses anything the player's
  // longest streak hasn't unlocked; we optimistically apply then roll back on
  // rejection so the UI feels instant but can't lie.
  async setCosmetics(patch) {
    const cur = get().profile
    if (!cur) return { ok: false, error: 'No profile' }
    const next: Profile = {
      ...cur,
      avatarBorder: patch.border ?? cur.avatarBorder,
      avatarBadge: patch.badge === undefined ? cur.avatarBadge : patch.badge,
    }
    set({ profile: next })

    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return { ok: true }
    }

    const { data, error } = await supabase.rpc('set_cosmetics', {
      p_border: next.avatarBorder,
      p_badge: next.avatarBadge ?? null,
    })
    if (error || !data?.ok) {
      set({ profile: cur }) // roll back the optimistic change
      return { ok: false, error: error?.message ?? 'That’s not unlocked yet' }
    }
    return { ok: true }
  },

  // Equip a player-card background. Like setCosmetics, the server is the gate:
  // it refuses any key whose collectible the account hasn't unlocked, so we
  // apply optimistically and roll back if it says no.
  async setCardBackground(key) {
    const cur = get().profile
    if (!cur) return { ok: false, error: 'No profile' }
    const next: Profile = { ...cur, cardBackground: key === 'default' ? null : key }
    set({ profile: next })

    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return { ok: true }
    }

    const { data, error } = await supabase.rpc('set_card_background', { p_key: key })
    if (error || !data?.ok) {
      set({ profile: cur }) // roll back the optimistic change
      return { ok: false, error: error?.message ?? 'That background isn’t unlocked yet' }
    }
    return { ok: true }
  },

  // Equip a pet. Same shape as setCosmetics/setCardBackground: optimistic, with
  // the server as the gate — it refuses any pet the account's level hasn't
  // reached, so the UI feels instant but can't lie. Guests keep theirs on the
  // device, where the level in the local profile is the same number the ladder
  // reads, so both modes gate on exactly the same thing.
  async setPet(id, progress) {
    const cur = get().profile
    if (!cur) return { ok: false, error: 'No profile' }
    const next: Profile = { ...cur, pet: id }
    set({ profile: next })

    if (get().mode === 'local' || !supabase) {
      // The local gate is the catalog itself — data/pets is the only ladder,
      // and lib/petProgress gathered the numbers it reads. `isAdmin` mirrors
      // the operator preview the online gate grants in 0067; on a guest profile
      // it is simply absent, so nothing changes for a normal player.
      if (id && progress && !petUnlocked(id, progress, !!cur.isAdmin)) {
        set({ profile: cur })
        return { ok: false, error: 'That one isn’t unlocked yet' }
      }
      localdb.saveProfile(next)
      return { ok: true }
    }

    const { data, error } = await supabase.rpc('set_pet', { p_pet: id })
    if (error || !data?.ok) {
      set({ profile: cur }) // roll back the optimistic change
      return { ok: false, error: error?.message ?? 'That one isn’t unlocked yet' }
    }
    return { ok: true }
  },

  // What the card says about you. Same shape as setCardBackground: normalise
  // against the catalogs first (so a guest gets exactly the refusal an account
  // would), apply optimistically, and let the server (0098) have the last word
  // online — rolling back if it says no.
  async setCardAbout(about) {
    const cur = get().profile
    if (!cur) return { ok: false, error: 'No profile' }
    const verse = about.verse ? normalizeVerseReference(about.verse) : null
    if (about.verse && !verse) return { ok: false, error: 'That isn’t a verse the Bible has' }
    const book = about.book ? normalizeBook(about.book) : null
    if (about.book && !book) return { ok: false, error: 'That isn’t one of the 66 books' }
    const translation = about.translation ? normalizeTranslation(about.translation) : null
    if (about.translation && !translation) return { ok: false, error: 'That translation isn’t on the list' }

    const next: Profile = { ...cur, favoriteVerse: verse, favoriteBook: book, favoriteTranslation: translation }
    set({ profile: next })

    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return { ok: true }
    }

    const { data, error } = await supabase.rpc('set_card_about', {
      p_verse: verse,
      p_book: book,
      p_translation: translation,
    })
    if (error || !data?.ok) {
      set({ profile: cur }) // roll back the optimistic change
      return { ok: false, error: error?.message ?? 'Couldn’t save that' }
    }
    return { ok: true }
  },

  // Equip the composable character avatar. Applied optimistically, then
  // persisted — on-device in LOCAL mode, to profiles.avatar_character online.
  setAvatarCharacter(spec) {
    const cur = get().profile
    if (!cur) return
    const next: Profile = { ...cur, avatarCharacter: spec }
    set({ profile: next })
    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return
    }
    void supabase
      .from('profiles')
      .update({ avatar_character: spec })
      .eq('id', cur.id)
      .then(({ error }) => {
        if (error) set({ error: error.message })
      })
  },

  // Record that the player shared a given day's drop (distinct days only). Drives
  // share-count unlocks like the King Baldwin set. Persists on-device in LOCAL
  // mode, to profiles.shared_days online.
  recordShare(dropDate) {
    const cur = get().profile
    if (!cur) return
    const days = cur.sharedDays ?? []
    if (days.includes(dropDate)) return // already counted this day
    const next: Profile = { ...cur, sharedDays: [...days, dropDate] }
    set({ profile: next })
    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return
    }
    void supabase
      .from('profiles')
      .update({ shared_days: next.sharedDays })
      .eq('id', cur.id)
      .then(({ error }) => {
        if (error) set({ error: error.message })
      })
  },

  // Grant a collected wearable item (from a Daily Chest drop). Distinct ids only.
  // Persists on-device in LOCAL mode, to profiles.owned_items online.
  grantItem(itemId) {
    const cur = get().profile
    if (!cur) return
    const owned = cur.ownedItems ?? []
    if (owned.includes(itemId)) return
    const next: Profile = { ...cur, ownedItems: [...owned, itemId] }
    set({ profile: next })
    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return
    }
    void supabase
      .from('profiles')
      .update({ owned_items: next.ownedItems })
      .eq('id', cur.id)
      .then(({ error }) => {
        if (error) set({ error: error.message })
      })
  },

  // Grant entitlement to a full-look skin. Today this is a free "preview" unlock
  // (no IAP yet); real purchases will write this set server-side later. Distinct
  // ids only; persists on-device in LOCAL mode, to profiles.owned_skins online.
  grantSkin(skinId) {
    const cur = get().profile
    if (!cur) return
    const owned = cur.ownedSkins ?? []
    if (owned.includes(skinId)) return
    const next: Profile = { ...cur, ownedSkins: [...owned, skinId] }
    set({ profile: next })
    if (get().mode === 'local' || !supabase) {
      localdb.saveProfile(next)
      return
    }
    void supabase
      .from('profiles')
      .update({ owned_skins: next.ownedSkins })
      .eq('id', cur.id)
      .then(({ error }) => {
        if (error) set({ error: error.message })
      })
  },

  async deleteAccount() {
    if (get().mode === 'local' || !supabase) {
      localdb.clear()
      set({ profile: null, isAuthed: false })
      return true
    }
    // Checked, because the old version signed out whatever the server said:
    // a failed deletion looked exactly like a successful one, and an account
    // a player believes is gone is the one thing this flow must never leave.
    const { error } = await supabase.rpc('delete_my_account')
    if (error) return false
    await supabase.auth.signOut()
    clearCachedProfile()
    set({ profile: null, isAuthed: false })
    return true
  },

  async signOut() {
    if (supabase) await supabase.auth.signOut()
    clearCachedProfile()
    set({ profile: null, isAuthed: false })
  },
}))

// Keep the device's copy current with every change an online profile takes —
// a play's XP, a new border — so the copy an offline launch opens on is the
// latest one this phone saw, not the one from the last full refresh.
// A queued prayer or arcade run that just landed was paid on the server.
onOutboxSent(() => { void useAuth.getState().refreshProfile() })

useAuth.subscribe((s) => {
  if (s.mode === 'online' && s.isAuthed && s.profile) writeCachedProfile(s.profile)
})
